/* eslint-disable @typescript-eslint/no-explicit-any */
import { Edge, Connection } from 'reactflow';
import { AnchorType } from '../types';

export type NodeConfig = {
  title: string;
  identifier: string;
  background: string;
  anchors: AnchorType[];
  command_id: string;
  trigger?: boolean;
  pair?: string;
  icon: string;
  version: string;
  description?: string;
  moduleId?: string | null;
  webhooks?: WebhookExecutionAnchor[];
  changelog?: string;
};

export type WebhookExecutionAnchor = {
  webhook: string;
  expected: boolean;
  multi: boolean;
  suffix?: string;
};

export type NodeField = {
  id: string;
  type: string;
  default_type?: NodeInputType;
  label: string;
  description: string;
  placeholder?: string;
  display_name?: boolean;
  required?: boolean;
  value?: any;
  validation?: FieldValidation;
  tab?: string;
  options?: { label: string; value: string }[];
  maxNumber?: number;
  minNumber?: number;
  multiSelect?: boolean;
};

export type NodeOutput = {
  default: { [key: string]: any } | null;
  dynamic: { [key: string]: any } | null;
  json_mode?: { [key: string]: any } | null;
};

export type NodeExport = {
  config: NodeConfig;
  state: NodeState;
  fields: NodeField[];
  output: NodeOutput;
};

type NodeStateDefault = { [key: string]: any };

export type NodeState = InboundCallState &
  SpeakTextNodeState &
  GatherUsingAINodeState &
  TranscriptionStartNodeState &
  TranscriptionStopNodeState &
  PlaybackStartNodeState &
  PlaybackStopNodeState &
  HangupNodeState &
  NodeStateDefault &
  RequestNodeState &
  ResponseNodeState &
  AICompletionNodeState &
  HTTPNodeState &
  ToolNodeState &
  SwitchNodeState &
  BranchNodeState &
  ModuleNodeState &
  ModNodeState &
  StoreNodeState &
  DelayNodeState &
  Base64TransformerNodeState &
  DateTimeState &
  AnswerNodeState &
  NoiseSuppressionState &
  NoiseSuppressionStopState &
  DialNodeState &
  RejectCallNodeState &
  BridgeNodeState &
  TransferCallNodeState &
  InboundMessageNodeState &
  RecordingStartNodeState &
  RecordingStopNodeState &
  RecordingPauseNodeState &
  RecordingResumeNodeState &
  SendMessageNodeState &
  AssistantNodeState &
  AssistantRouterNodeState &
  JumpToNodeState;

export enum FieldValidation {
  default = 'default',
  variable = 'variable',
}

export type NodeType = {
  id: string;
  selected: boolean;
  data: NodeDataType;
};

export type NodeDataType = {
  edges?: Edge[];
  sources: Connection[];
  targets: Connection[];
};

export enum NodeInputType {
  Text = 'fixed_text',
  Option = 'fixed_option',
  Expression = 'expression',
  MessageExpression = 'message_expression',
  Model = 'model',
  Node = 'node',
  ElevenLabsVoice = 'elevenlabs_voice',
  Timezone = 'timezone',
  Number = 'number',
  CallHeader = 'call_header',
  MediaUrl = 'media_url',
}

// used when the field can be an expression
export type NodeInput<T> = {
  type: NodeInputType;
  value: T;
};

export type NodeAIArgument = {
  id: string;
  name: string;
  description: string;
  type: 'string' | 'boolean' | 'enum' | 'number';
  enumerator: string;
  required?: 'true' | 'false';
};

export type NodeRequestParameter<T extends NodeInput<string> | string = NodeInput<string>> = {
  id: string;
  name: string;
  value: T extends NodeInput<string> ? NodeInput<string> : string;
  type?: 'body' | 'query' | 'header';
  required?: boolean;
};

export type NodeRequestAuth = {
  enabled: boolean;
  type: NodeInput<'basic' | 'bearer'>;
  name: NodeInput<string>;
  value: NodeInput<string>;
  secret: boolean;
  secretUsername: string;
  secretHeader: string;
  secretUsage: 'bearer' | 'basic';
  secretName: string;
};

export type RequestNodeState = {
  method: 'get' | 'post' | 'patch' | 'delete' | 'put';
  query_parameters: NodeRequestParameter<string>[];
  body_parameters: NodeRequestParameter<string>[];
};

export type InboundMessageNodeState = {
  messaging_profile_id: string;
};

export type MediaUrl = { id: string; value: string };

export type SendMessageNodeState = {
  messaging_profile_id: string;
  from: NodeInput<string>;
  to: NodeInput<string>;
  text: NodeInput<string>;
  subject: NodeInput<string>;
  media_urls: NodeInput<MediaUrl[]>;
  webhook_url: NodeInput<string>;
  webhook_failover_url: NodeInput<string>;
  use_profile_webhooks: 'true' | 'false';
  type: 'SMS' | 'MMS';
  auto_detect: 'true' | 'false';
};

export type InboundCallState = {
  connection_id: string;
};

export type HTTPNodeState = {
  method: 'get' | 'post' | 'patch' | 'delete' | 'put';
  url: NodeInput<string>;
  query_parameters: NodeRequestParameter[];
  header_parameters: NodeRequestParameter[];
  body_format: 'json' | 'form';
  body_parameters: NodeRequestParameter[];
  body_json: NodeInput<string>;
  auth: NodeRequestAuth;
  on_error: string;
  parse_response_as: string;
};

export type ResponseNodeState = {
  response_method: 'async' | 'stream';
  format_response: 'true' | 'false';
  format_schema: NodeInput<string>;
};

export type AICompletionJsonMode = {
  enabled: boolean;
  args: NodeAIArgument[];
};

export type ModuleParameter = {
  id: string;
  name: string;
  description: string;
  value: string;
  type: 'string' | 'boolean' | 'enum' | 'number';
  required: boolean;
};

export type ModNodeState = {
  moduleId: string;
  [key: string]: NodeInput<string> | string;
};

export type ModuleNodeState = {
  description: string;
  parameters: ModuleParameter[];
};

export type AssistantNodeState = {
  name: string;
  session_id: NodeInput<string>;
  include_session_tools: 'true' | 'false';
  query: NodeInput<string>;
  assistant_id: string;
  sender_id: NodeInput<string>;
  files: NodeInput<string>;
};

export type AssistantRouterNodeState = {
  model: string;
  name: string;
  router_method: 'route_and_answer' | 'route_and_return';
  rewrite_question: 'true' | 'false';
  session_id: NodeInput<string>;
  include_session_tools: 'true' | 'false';
  sender_id: NodeInput<string>;
  query: NodeInput<string>;
  system: NodeInput<string>;
  assistants: string[];
  files: NodeInput<string>;
};

export type AICompletionNodeState = {
  model: string;
  name: string;
  user_id: NodeInput<string>;
  session_id: NodeInput<string>;
  message_storage: NodeInput<string>;
  query: NodeInput<string>;
  system: NodeInput<string>;
  messages: NodeInput<string>;
  json_mode: AICompletionJsonMode;
  message_array: {
    enabled: boolean;
  };
  after: {
    tool_choice: NodeInput<string>;
    show_feedback: NodeInput<string>;
    show_help_action: NodeInput<string>;
    max_tool_calls?: NodeInput<string>;
    max_tool_reached_system?: NodeInput<string>;
    max_conversation_history?: NodeInput<string>;
  };
  low_latency_beta: 'true' | 'false';
};

export type RecordingStartNodeState = {
  call_control_id: NodeInput<string>;
  client_state: NodeInput<string>;
  format: 'wav' | 'mp3';
  channels: 'single' | 'dual';
  play_beep: 'true' | 'false';
  max_length: NodeInput<string>;
  timeout_secs: NodeInput<string>;
  recording_track: 'both' | 'inbound' | 'outbound';
  trim: 'disabled' | 'trim-silence';
  custom_file_name: NodeInput<string>;
  transcription: 'true' | 'false';
  transcription_engine: 'A' | 'B';
  transcription_language_telnyx: string;
  transcription_language_google: string;
  transcription_profanity_filter: 'true' | 'false';
  transcription_speaker_diarization: 'true' | 'false';
  transcription_min_speaker_count: NodeInput<string>;
  transcription_max_speaker_count: NodeInput<string>;
};

export type RecordingStopNodeState = {
  call_control_id: NodeInput<string>;
  recording_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type RecordingResumeNodeState = {
  call_control_id: NodeInput<string>;
  recording_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type RecordingPauseNodeState = {
  call_control_id: NodeInput<string>;
  recording_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type ToolNodeState = {
  name: string;
  description: NodeInput<string>;
  system: NodeInput<string>;
  args: NodeAIArgument[];
  after: {
    tool_choice: NodeInput<string>;
    show_feedback: NodeInput<string>;
    show_help_action: NodeInput<string>;
  };
  http_request: {
    enabled: boolean;
    method: NodeInput<'get' | 'post'>;
    url: NodeInput<string>;
    query_parameters: NodeRequestParameter[];
    header_parameters: NodeRequestParameter[];
    body_format: 'json' | 'form';
    body_parameters: NodeRequestParameter[];
    body_json: NodeInput<string>;
    auth_enabled: boolean;
    auth_secret_name: string;
  };
  bucket_search: {
    enabled: boolean;
    search_descriptor: NodeInput<string>;
    bucket_search_type: NodeInput<string>;
    max_tokens: number;
    max_results: number;
    backup_output: NodeInput<string>;
    backup_tool_choice: NodeInput<string>;
    indexes: {
      id: string;
      index: string;
      weight: string;
      type: 'weaviate' | 'telnyx';
    }[];
  };
  api_search: {
    enabled: boolean;
    search_descriptor: NodeInput<string>;
    bucket_search_type: NodeInput<string>;
    max_tokens: number;
    max_results: number;
    query_parameters: NodeInput<string>;
    body_parameters: NodeInput<string>;
    responses: NodeInput<string>;
    webhooks: NodeInput<string>;
    sdk: NodeInput<string>;
    backup_output: NodeInput<string>;
    backup_tool_choice: NodeInput<string>;
  };
  pricing_calculator: {
    enabled: boolean;
    product: NodeInput<string>;
    countryCode: NodeInput<string>;
  };
  custom_output: {
    enabled: boolean;
    output: NodeInput<string>;
  };
};

export type SwitchNodeState = {
  name: string;
  condition_groups: NodeConditionGroup[];
};

export type StoreNodeState = {
  session_id: NodeInput<string>;
  user_id: NodeInput<string>;
  user_message: NodeInput<string>;
  bot_message: NodeInput<string>;
};

export type DelayNodeState = {
  delay: NodeInput<string>;
};

export type Base64TransformerNodeState = {
  operation: 'encode' | 'decode';
  input: NodeInput<string>;
};

export type DateTimeState = {
  timezone: string;
};

export type BranchNodeState = {
  name: string;
  condition: {
    name: string;
    input: string;
    operator: string;
    value: string;
  };
};

export type JumpToNodeState = {
  node_id: string;
  max_jumps: NodeInput<string>;
};

export type SpeakTextNodeState = {
  call_control_id: NodeInput<string>;
  payload: NodeInput<string>;
  payload_type: NodeInput<'text' | 'ssml'>;
  service_level: NodeInput<'basic' | 'premium'>;
  stop: NodeInput<'current' | 'all' | ''>;
  voice: NodeInput<'ElevenLabs' | 'AWS.Polly' | 'male' | 'female' | 'Telnyx.KokoroTTS' | 'Azure'>;
  elevenlabs_api_key: string;
  elevenlabs_voice_id: string;
  elevenlabs_model_id: string;
  aws_polly_voice_id: string;
  aws_polly_neural: 'standard' | 'neural' | 'generative' | 'long_form';
  kokoro_voice_id: string;
  azure_voice_id: string;
  client_state: NodeInput<string>;
  language: NodeInput<
    | 'arb'
    | 'cmn-CN'
    | 'cy-GB'
    | 'da-DK'
    | 'de-DE'
    | 'en-AU'
    | 'en-GB'
    | 'en-GB-WLS'
    | 'en-IN'
    | 'en-US'
    | 'es-ES'
    | 'es-MX'
    | 'es-US'
    | 'fr-CA'
    | 'fr-FR'
    | 'hi-IN'
    | 'is-IS'
    | 'it-IT'
    | 'ja-JP'
    | 'ko-KR'
    | 'nb-NO'
    | 'nl-NL'
    | 'pl-PL'
    | 'pt-BR'
    | 'pt-PT'
    | 'ro-RO'
    | 'ru-RU'
    | 'sv-SE'
    | 'tr-T'
  >;
  // client_state: NodeInput<string>;
  // command_id: NodeInput<string>;
};

export type GatherUsingAINodeState = {
  model: string;
  instructions: NodeInput<string>;
  call_control_id: NodeInput<string>;
  voice: NodeInput<'Telnyx.LibriTTS.0' | 'ElevenLabs' | 'AWS.Polly' | 'Telnyx.KokoroTTS' | 'Azure'>;
  args: NodeAIArgument[];
  greeting: NodeInput<string>;
  message_history: NodeInput<string>;
  client_state: NodeInput<string>;
  gather_ended_speech: NodeInput<string>;
  voice_speed: NodeInput<string>;
  kokoro_voice_id: string;
  azure_voice_id: string;
  elevenlabs_api_key: string;
  elevenlabs_voice_id: string;
  elevenlabs_model_id: string;
  aws_polly_voice_id: string;
  aws_polly_neural: 'standard' | 'neural' | 'generative' | 'long_form';
  send_partial_results: 'true' | 'false';
  interruption_enabled: 'true' | 'false';
  transcription_model: 'distil-whisper/distil-large-v2' | 'openai/whisper-large-v3-turbo' | 'google';
  language_google: string;
};

export type TranscriptionStartNodeState = {
  call_control_id: NodeInput<string>;
  transcription_engine: NodeInput<'A' | 'B'>;
  language_telnyx: string;
  language_google: string;
  transcription_tracks: NodeInput<'inbound' | 'outbound' | 'both'>;
  interrupt_playback: NodeInput<'true' | 'false'>;
  interim_results: NodeInput<'true' | 'false'>;
  client_state: NodeInput<string>;
};

export type TranscriptionStopNodeState = {
  call_control_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type DialNodeState = {
  to: NodeInput<string>;
  from: NodeInput<string>;
  from_display_name: NodeInput<string>;
  preferred_codecs: ('G722' | 'PCMU' | 'PCMA' | 'G729' | 'OPUS' | 'VP8' | 'H264')[];
  audio_url: NodeInput<string>;
  media_name: NodeInput<string>;
  media_encryption: 'disabled' | 'SRTP';
  timeout_secs: NodeInput<string>;
  time_limit_secs: NodeInput<string>;
  billing_group_id: NodeInput<string>;
  send_silence_when_idle: NodeInput<string>;
  sip_transport_protocol: 'UDP' | 'TCP' | 'TLS';
  sip_auth_username: NodeInput<string>;
  sip_auth_password: NodeInput<string>;
  client_state: NodeInput<string>;
  // transcription
  transcription: NodeInput<string>;
  transcription_engine: NodeInput<'A' | 'B'>;
  language_telnyx: string;
  language_google: string;
  transcription_tracks: NodeInput<'inbound' | 'outbound' | 'both'>;
  interrupt_playback: NodeInput<'true' | 'false'>;
  // streaming
  stream_url: NodeInput<string>;
  stream_track: 'inbound_track' | 'outbound_track' | 'both_tracks';
  stream_bidirectional_mode: 'mp3' | 'rtp';
  stream_bidirectional_codec: 'PCMU' | 'PCMA' | 'G722';
  stream_bidirectional_target_legs: 'both' | 'self' | 'opposite';
  // headers
  custom_headers: NodeInput<CallHeader[]>;
  sip_headers: NodeInput<CallHeader[]>;
  // answering machine detection
  answering_machine_detection: 'premium' | 'detect' | 'detect_beep' | 'detect_words' | 'greeting_end' | 'disabled';
  total_analysis_time_millis: NodeInput<string>;
  after_greeting_silence_millis: NodeInput<string>;
  between_words_silence_millis: NodeInput<string>;
  greeting_duration_millis: NodeInput<string>;
  initial_silence_millis: NodeInput<string>;
  maximum_number_of_words: NodeInput<string>;
  maximum_word_length_millis: NodeInput<string>;
  silence_threshold: NodeInput<string>;
  greeting_total_analysis_time_millis: NodeInput<string>;
  greeting_silence_duration_millis: NodeInput<string>;
  // sound modifications
  sound_modifications_enabled: 'true' | 'false';
  pitch: NodeInput<string>;
  semitone: NodeInput<string>;
  octaves: NodeInput<string>;
  track: 'inbound' | 'outbound';
  // dialogflow
  enable_dialogflow: 'true' | 'false';
  analyze_sentiment: 'true' | 'false';
  partial_automated_agent_reply: 'true' | 'false';
};

export type TransferCallNodeState = {
  call_control_id: NodeInput<string>;
  to: NodeInput<string>;
  from: NodeInput<string>;
  from_display_name: NodeInput<string>;
  audio_url: NodeInput<string>;
  media_name: NodeInput<string>;
  media_encryption: 'disabled' | 'SRTP';
  timeout_secs: NodeInput<string>;
  time_limit_secs: NodeInput<string>;
  billing_group_id: NodeInput<string>;
  send_silence_when_idle: NodeInput<string>;
  sip_transport_protocol: 'UDP' | 'TCP' | 'TLS';
  sip_auth_username: NodeInput<string>;
  sip_auth_password: NodeInput<string>;
  early_media: 'true' | 'false';
  client_state: NodeInput<string>;
  target_leg_client_state: NodeInput<string>;
  // headers
  custom_headers: NodeInput<CallHeader[]>;
  sip_headers: NodeInput<CallHeader[]>;
  // answering machine detection
  answering_machine_detection: 'premium' | 'detect' | 'detect_beep' | 'detect_words' | 'greeting_end' | 'disabled';
  total_analysis_time_millis: NodeInput<string>;
  after_greeting_silence_millis: NodeInput<string>;
  between_words_silence_millis: NodeInput<string>;
  greeting_duration_millis: NodeInput<string>;
  initial_silence_millis: NodeInput<string>;
  maximum_number_of_words: NodeInput<string>;
  maximum_word_length_millis: NodeInput<string>;
  silence_threshold: NodeInput<string>;
  greeting_total_analysis_time_millis: NodeInput<string>;
  greeting_silence_duration_millis: NodeInput<string>;
  // sound modifications
  sound_modifications_enabled: 'true' | 'false';
  pitch: NodeInput<string>;
  semitone: NodeInput<string>;
  octaves: NodeInput<string>;
  track: 'inbound' | 'outbound';
};

export type CallHeader = { id: string; name: string; value: string };

export type BridgeNodeState = {
  call_control_id: NodeInput<string>;
  target_call_control_id: NodeInput<string>;
  client_state: NodeInput<string>;
  play_ringtone: 'true' | 'false';
  ringtone: NodeInput<string>;
};

export type AnswerNodeState = {
  call_control_id: NodeInput<string>;
  billing_group_id: NodeInput<string>;
  send_silence_when_idle: NodeInput<string>;
  client_state: NodeInput<string>;
  preferred_codecs: ('G722' | 'PCMU' | 'PCMA' | 'G729' | 'OPUS' | 'VP8' | 'H264')[];
  // streaming
  stream_url: NodeInput<string>;
  stream_track: 'inbound_track' | 'outbound_track' | 'both_tracks';
  stream_bidirectional_mode: 'mp3' | 'rtp';
  stream_bidirectional_codec: 'PCMU' | 'PCMA' | 'G722';
  stream_bidirectional_target_legs: 'both' | 'self' | 'opposite';
  // transcription
  transcription: NodeInput<string>;
  transcription_engine: NodeInput<'A' | 'B'>;
  language_telnyx: string;
  language_google: string;
  transcription_tracks: NodeInput<'inbound' | 'outbound' | 'both'>;
  interrupt_playback: NodeInput<'true' | 'false'>;
  interim_results: NodeInput<'true' | 'false'>;
  // headers
  custom_headers: NodeInput<CallHeader[]>;
  sip_headers: NodeInput<CallHeader[]>;
  // sound modifications
  sound_modifications_enabled: 'true' | 'false';
  pitch: NodeInput<string>;
  semitone: NodeInput<string>;
  octaves: NodeInput<string>;
  track: 'inbound' | 'outbound';
  // recording disabled as recording_id is not returned by API request to answer call
  // record: 'record-from-answer' | 'false';
  // record_format: 'wav' | 'mp3';
  // record_channels: 'single' | 'dual';
  // record_play_beep: 'true' | 'false';
  // record_max_length: NodeInput<string>;
  // record_timeout_secs: NodeInput<string>;
  // record_track: 'both' | 'inbound' | 'outbound';
  // record_trim: 'disabled' | 'trim-silence';
  // record_custom_file_name: NodeInput<string>;
};

export type StreamingStartNodeState = {
  call_control_id: NodeInput<string>;
  client_state: NodeInput<string>;
  // streaming
  stream_url: NodeInput<string>;
  stream_track: 'inbound_track' | 'outbound_track' | 'both_tracks';
  stream_bidirectional_mode: 'mp3' | 'rtp';
  stream_bidirectional_codec: 'PCMU' | 'PCMA' | 'G722';
  stream_bidirectional_target_legs: 'both' | 'self' | 'opposite';
  // dialogflow
  enable_dialogflow: 'true' | 'false';
  analyze_sentiment: 'true' | 'false';
  partial_automated_agent_reply: 'true' | 'false';
};

export type StreamingStopNodeState = {
  call_control_id: NodeInput<string>;
  stream_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type RejectCallNodeState = {
  call_control_id: NodeInput<string>;
  cause: 'CALL_REJECTED' | 'USER_BUSY';
  client_state: NodeInput<string>;
};

export type NoiseSuppressionState = {
  call_control_id: NodeInput<string>;
  direction: NodeInput<'both' | 'inbound' | 'outbound'>;
  client_state: NodeInput<string>;
};

export type NoiseSuppressionStopState = {
  call_control_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type PlaybackStartNodeState = {
  call_control_id: NodeInput<string>;
  audio_url: NodeInput<string>;
  media_name: NodeInput<string>;
  loop: NodeInput<string>;
  overlay: NodeInput<'true' | 'false' | ''>;
  stop: NodeInput<'current' | 'all' | ''>;
  target_legs: NodeInput<'self' | 'opposite' | 'both'>;
  cache_audio: NodeInput<'true' | 'false'>;
  audio_type: NodeInput<'mp3' | 'wav'>;
  playback_content: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type PlaybackStopNodeState = {
  call_control_id: NodeInput<string>;
  overlay: NodeInput<'true' | 'false' | ''>;
  stop: NodeInput<'current' | 'all' | ''>;
  client_state: NodeInput<string>;
};

export type HangupNodeState = {
  call_control_id: NodeInput<string>;
  client_state: NodeInput<string>;
};

export type NodeConditionGroup = {
  id: string;
  name: string;
  operator: 'and' | 'or';
  conditions: NodeCondition[];
};

export type NodeCondition = {
  id: string;
  input: string;
  operator: string;
  value: string;
};

export type NodeConditionOperator =
  | 'equals'
  | 'is_undefined'
  | 'is_defined'
  | 'starts_with'
  | 'ends_with'
  | 'contains'
  | 'does_not_equal'
  | 'does_not_start_with'
  | 'does_not_end_with'
  | 'does_not_contain';

export enum NodeFieldType {
  // these fields are compatible with the JumpTo node
  Option = 'option',
  Input = 'input',
  NumberExpression = 'expression_number',
  InputExpression = 'expression_input',
  OptionExpression = 'expression_option',
  // ---
  ModuleParameter = 'module_parameter',
  MessageArrayInputExpression = 'expression_input',
  ToggleJSON = 'toggle_json',
  ToolArgs = 'tool_args',
  After = 'after',
  WorkflowEndpoint = 'workflow_endpoint',
  RequestParameter = 'request_parameter',
  RequestParameterExpression = 'request_parameter_exp',
  CallHeaders = 'call_headers',
  Toggle = 'toggle',
  Switch = 'switch',
  Condition = 'condition',
  RequestAuth = 'request_auth',
  VoiceApplication = 'voice_application',
  Schema = 'schema',
  Secret = 'secret',
  MessagingProfile = 'messaging_profile',
  MediaUrls = 'media_urls',
  AssistantRouter = 'assistant_router',
  ModelWithTools = 'model_with_tools',
}

export type NodeIndex = {
  id: string;
  index: string;
  weight: number;
  type?: string | null;
};
